// src/components/Header.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, User, LogOut, Heart, Menu, X, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import logoImg from '../assets/cider.clo.wb.png';


const Header = () => {
  const { isLoggedIn, logout } = useAuth();
  const { cartItemCount, wishlistItemCount } = useCart();
  const { currency, setCurrency } = useCurrency();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Animation state for cart and wishlist icons
  const [cartAnimation, setCartAnimation] = useState(false);
  const [wishlistAnimation, setWishlistAnimation] = useState(false);
  const [prevCartCount, setPrevCartCount] = useState(0);
  const [prevWishlistCount, setPrevWishlistCount] = useState(0);

  const drawerRef = useRef(null);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  // Lock body scroll while drawer is open; close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        setMobileMenuOpen(false);
      }
    };
    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Animate cart icon when count increases
  useEffect(() => {
    if (cartItemCount > prevCartCount && prevCartCount > 0) {
      setCartAnimation(true);
      setTimeout(() => setCartAnimation(false), 600);
    }
    setPrevCartCount(cartItemCount);
  }, [cartItemCount, prevCartCount]);

  // Animate wishlist icon when count increases
  useEffect(() => {
    if (wishlistItemCount > prevWishlistCount && prevWishlistCount >= 0) {
      setWishlistAnimation(true);
      setTimeout(() => setWishlistAnimation(false), 600);
    }
    setPrevWishlistCount(wishlistItemCount);
  }, [wishlistItemCount, prevWishlistCount]);

  const searchParams = new URLSearchParams(location.search);
  const categoryParam = searchParams.get('category');

  const isNavActive = (path, category = null) => {
    if (category) return location.pathname === path && categoryParam === category;
    if (path === '/products') return location.pathname === '/products' && !categoryParam;
    return location.pathname === path;
  };

  const activeLinkStyle = {
    color: '#4f46e5',
    textDecoration: 'underline',
    textUnderlineOffset: '4px',
  };

  const navLinks = [
    { label: 'Home',   to: '/',                    active: isNavActive('/') },
    { label: 'Unisex', to: '/products',             active: isNavActive('/products') },
    { label: 'Men',    to: '/products?category=men',   active: isNavActive('/products', 'men') },
    { label: 'Women',  to: '/products?category=women', active: isNavActive('/products', 'women') },
  ];

  return (
    <>
      {/* ── Main Header Bar ────────────────────────────────────────── */}
      <header className="sticky top-0 z-30 bg-white bg-opacity-90 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 items-center h-16">

            {/* Hamburger — mobile only (left column) */}
            <div className="flex items-center md:hidden">
              <button
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open menu"
                className="text-gray-700 hover:text-indigo-600 transition-colors p-1 -ml-1"
              >
                <Menu size={24} />
              </button>
            </div>

            {/* Logo — centred on mobile, left on desktop */}
            <div className="flex items-center justify-center md:justify-start h-full md:-ml-6">
              <Link to="/" className="flex items-center">
                <img
                  src={logoImg}
                  alt="Cider Clothing"
                  className="h-16 w-auto scale-[2.5] origin-center md:origin-left object-contain mix-blend-multiply"
                />
              </Link>
            </div>

            {/* Desktop Nav — hidden on mobile */}
            <nav className="hidden md:flex md:space-x-8 justify-center">
              {navLinks.map(({ label, to, active }) => (
                <Link
                  key={to}
                  to={to}
                  className="text-gray-700 hover:text-indigo-600 font-medium transition-colors"
                  style={active ? activeLinkStyle : undefined}
                >
                  {label}
                </Link>
              ))}
            </nav>

            {/* Right Icons */}
            <div className="flex items-center justify-end space-x-3 sm:space-x-4 md:space-x-6">

              {/* Currency Switcher — desktop only */}
              <div className="hidden md:flex items-center bg-gray-100 rounded-full p-0.5 text-sm font-medium">
                {['LKR', 'USD'].map((cur) => (
                  <button
                    key={cur}
                    onClick={() => setCurrency(cur)}
                    className={`px-3 py-1 rounded-full transition-all ${
                      currency === cur
                        ? 'bg-indigo-600 text-white shadow'
                        : 'text-gray-500 hover:text-indigo-600'
                    }`}
                  >
                    {cur}
                  </button>
                ))}
              </div>

              {/* User / Sign-in icon */}
              <Link
                to={isLoggedIn ? '/dashboard' : '/signin'}
                className="text-gray-600 hover:text-indigo-600 transition-colors"
                title={isLoggedIn ? 'My Dashboard' : 'Sign In'}
              >
                <User size={22} />
              </Link>

              {/* Wishlist — logged-in only */}
              {isLoggedIn && (
                <Link
                  to="/wishlist"
                  className={`relative text-gray-600 hover:text-indigo-600 transition-all ${wishlistAnimation ? 'animate-bounce' : ''}`}
                  title="My Wishlist"
                >
                  <Heart size={22} />
                  {wishlistItemCount > 0 && (
                    <span
                      className={`absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium transition-all ${wishlistAnimation ? 'animate-pulse scale-110' : ''}`}
                    >
                      {wishlistItemCount > 99 ? '99+' : wishlistItemCount}
                    </span>
                  )}
                  {wishlistAnimation && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-400 rounded-full animate-ping" />
                  )}
                </Link>
              )}

              {/* Cart */}
              <Link
                to="/cart"
                className={`relative text-gray-600 hover:text-indigo-600 transition-all ${cartAnimation ? 'animate-bounce' : ''}`}
                title="Shopping Cart"
              >
                <ShoppingBag size={22} />
                {cartItemCount > 0 && (
                  <span
                    className={`absolute -top-2 -right-2 bg-indigo-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium transition-all ${cartAnimation ? 'animate-pulse scale-110' : ''}`}
                  >
                    {cartItemCount > 99 ? '99+' : cartItemCount}
                  </span>
                )}
                {cartAnimation && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-indigo-400 rounded-full animate-ping" />
                )}
              </Link>

              {/* Logout — desktop only; mobile uses the drawer */}
              {isLoggedIn && (
                <button
                  onClick={logout}
                  className="hidden md:block text-gray-600 hover:text-indigo-600 transition-colors"
                  title="Sign Out"
                >
                  <LogOut size={22} />
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ── Mobile Drawer Backdrop ─────────────────────────────────── */}
      <div
        onClick={() => setMobileMenuOpen(false)}
        className={`fixed inset-0 z-40 bg-black transition-opacity duration-300 md:hidden ${
          mobileMenuOpen ? 'opacity-50 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden="true"
      />

      {/* ── Mobile Slide-in Drawer ─────────────────────────────────── */}
      <aside
        ref={drawerRef}
        className={`fixed top-0 left-0 z-50 h-full w-72 bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out md:hidden ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label="Mobile navigation"
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-5 h-16 border-b border-gray-100 flex-shrink-0">
          <Link to="/" className="flex items-center" onClick={() => setMobileMenuOpen(false)}>
            <img
              src={logoImg}
              alt="Cider Clothing"
              className="h-10 w-auto object-contain mix-blend-multiply"
            />
          </Link>
          <button
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
            className="text-gray-500 hover:text-indigo-600 transition-colors p-1"
          >
            <X size={22} />
          </button>
        </div>

        {/* Scrollable nav links */}
        <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-1">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest px-3 mb-3">
            Browse
          </p>
          {navLinks.map(({ label, to, active }) => (
            <Link
              key={to}
              to={to}
              className={`flex items-center justify-between px-3 py-3 rounded-xl font-medium transition-all ${
                active
                  ? 'bg-indigo-50 text-indigo-600'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600'
              }`}
            >
              <span>{label}</span>
              <ChevronRight size={16} className="text-gray-400" />
            </Link>
          ))}

          <div className="border-t border-gray-100 my-4" />

          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest px-3 mb-3">
            Account
          </p>
          {isLoggedIn ? (
            <>
              <Link
                to="/dashboard"
                className="flex items-center justify-between px-3 py-3 rounded-xl font-medium text-gray-700 hover:bg-gray-50 hover:text-indigo-600 transition-all"
              >
                <span>My Dashboard</span>
                <ChevronRight size={16} className="text-gray-400" />
              </Link>
              <Link
                to="/wishlist"
                className="flex items-center justify-between px-3 py-3 rounded-xl font-medium text-gray-700 hover:bg-gray-50 hover:text-indigo-600 transition-all"
              >
                <span>
                  Wishlist{wishlistItemCount > 0 ? ` (${wishlistItemCount})` : ''}
                </span>
                <ChevronRight size={16} className="text-gray-400" />
              </Link>
            </>
          ) : (
            <Link
              to="/signin"
              className="flex items-center justify-between px-3 py-3 rounded-xl font-medium text-gray-700 hover:bg-gray-50 hover:text-indigo-600 transition-all"
            >
              <span>Sign In</span>
              <ChevronRight size={16} className="text-gray-400" />
            </Link>
          )}
        </nav>

        {/* Drawer footer: currency + logout */}
        <div className="px-5 py-5 border-t border-gray-100 space-y-4 flex-shrink-0">
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">
              Currency
            </p>
            <div className="flex items-center bg-gray-100 rounded-full p-0.5 text-sm font-medium w-fit">
              {['LKR', 'USD'].map((cur) => (
                <button
                  key={cur}
                  onClick={() => setCurrency(cur)}
                  className={`px-4 py-1.5 rounded-full transition-all ${
                    currency === cur
                      ? 'bg-indigo-600 text-white shadow'
                      : 'text-gray-500 hover:text-indigo-600'
                  }`}
                >
                  {cur}
                </button>
              ))}
            </div>
          </div>

          {isLoggedIn && (
            <button
              onClick={() => { logout(); setMobileMenuOpen(false); }}
              className="flex items-center gap-2 w-full px-3 py-3 rounded-xl text-red-500 hover:bg-red-50 font-medium transition-all"
            >
              <LogOut size={18} />
              <span>Sign Out</span>
            </button>
          )}
        </div>
      </aside>
    </>
  );
};

export default Header;